import {Component, EventEmitter, Input, Output} from '@angular/core';
import {AbstractComponent} from "../../abstract.component";
import {
  AttendanceBonusType,
  IndividualScoringConfiguration, Lookups,
  VariableAttendanceBonus
} from "@model";
import {TranslocoService} from "@ngneat/transloco";
import {NgbActiveModal} from "@ng-bootstrap/ng-bootstrap";
import {FormArray, FormBuilder, FormControl, FormGroup} from "@angular/forms";
import {required} from "@utility";

@Component({
  selector: 'app-individual-scoring-config-edit-modal',
  templateUrl: './individual-scoring-config-edit-modal.component.html',
  styleUrl: './individual-scoring-config-edit-modal.component.scss',
  standalone: false
})
export class IndividualScoringConfigEditModalComponent extends AbstractComponent {

  @Input() scoringConfiguration: IndividualScoringConfiguration | undefined;
  @Output() onSave: EventEmitter<IndividualScoringConfiguration> = new EventEmitter<IndividualScoringConfiguration>();
  @Input() scoringDefinitions: Lookups[] = [];
  @Input() attendanceBonusTypes: Lookups[] = [];
  @Input() disableEditing: boolean = true;


  constructor(
      public translocoService: TranslocoService,
      public activeModal: NgbActiveModal,
      public formBuilder: FormBuilder,
  ) {
    super();
  }

  inputForm = new FormGroup({
    name: new FormControl(),
    scoringDefinition: new FormControl(),
    minFinishedRacesForScoring: new FormControl(),
    maxFinishedRacesForScoring: new FormControl(),
    attendanceBonusType: new FormControl(),
    fixedAttendanceBonus: new FormControl(),
    variableAttendanceBonuses: this.formBuilder.array([]),
    registrationDeadline: new FormControl(),
  });

  ngOnInit() {
    this.initForm();
  }

  initForm() {
    if (!this.disableEditing) {
      this.subscribeToFormValueChanges();
    }
    this.inputForm.controls.name.addValidators([required]);
    this.inputForm.controls.scoringDefinition.addValidators([required]);
    this.inputForm.controls.maxFinishedRacesForScoring.addValidators([required]);
    this.inputForm.controls.minFinishedRacesForScoring.addValidators([required]);
    this.inputForm.controls.attendanceBonusType.addValidators([required]);

    if ((this.scoringDefinitions.length ?? 0) === 0) {
      this.inputForm.controls.scoringDefinition.disable();
    }

    this.inputForm.setValidators([
      this.minLessThanMaxValidator(),
      this.variableAttendanceBonusRaceNumberValidator()
    ]);

    this.setFormValues();
    if (this.disableEditing) {
      this.inputForm.disable()
    }
  }

  private subscribeToFormValueChanges() {
    this.subscriptions.push(
        this.inputForm.controls.attendanceBonusType.valueChanges.subscribe((val) => {
          if (val === AttendanceBonusType.FIXED_ATTENDANCE_BONUS_POINTS) {
            this.inputForm.controls.fixedAttendanceBonus.addValidators([required]);
            this.inputForm.controls.fixedAttendanceBonus.enable();

            this.inputForm.controls.variableAttendanceBonuses.removeValidators([required]);
            this.clearAttendanceBonus();
            this.inputForm.controls.variableAttendanceBonuses.disable();
          }
          else if (val === AttendanceBonusType.VARIABLE_ATTENDANCE_BONUS_POINTS) {
            this.inputForm.controls.variableAttendanceBonuses.addValidators([required]);
            this.inputForm.controls.variableAttendanceBonuses.enable();
            if (!((this.variableAttendanceBonuses.controls.length ?? 0 ) > 0)) {
              this.addVariableAttendanceBonus();
            }

            this.inputForm.controls.fixedAttendanceBonus.removeValidators([required]);
            this.inputForm.controls.fixedAttendanceBonus.setValue(undefined);
            this.inputForm.controls.fixedAttendanceBonus.disable();
          }
          else {
            this.inputForm.controls.fixedAttendanceBonus.removeValidators([required]);
            this.inputForm.controls.fixedAttendanceBonus.setValue(undefined);
            this.inputForm.controls.fixedAttendanceBonus.disable();

            this.inputForm.controls.variableAttendanceBonuses.removeValidators([required]);
            this.clearAttendanceBonus();
            this.inputForm.controls.variableAttendanceBonuses.disable();
          }

          this.inputForm.updateValueAndValidity();
        })
    );

    this.subscriptions.push(
        this.inputForm.controls.maxFinishedRacesForScoring.valueChanges.subscribe(() => {
          this.inputForm.updateValueAndValidity();
        })
    );
  }

  private setFormValues() {
    if (this.scoringConfiguration) {
      this.scoringConfiguration.variableAttendanceBonuses?.forEach(value => {
        this.variableAttendanceBonuses.push(
            this.formBuilder.group({
              id: [value?.id ?? null],
              raceNumber: [value.raceNumber, required],
              bonusPoints: [value.bonusPoints ?? null, required],
            })
        );
      });
      this.inputForm.controls.name.setValue(this.scoringConfiguration.name);
      this.inputForm.controls.scoringDefinition.setValue(this.scoringConfiguration.scoringDefinition?.id);
      this.inputForm.controls.minFinishedRacesForScoring.setValue(this.scoringConfiguration.minFinishedRacesForScoring);
      this.inputForm.controls.maxFinishedRacesForScoring.setValue(this.scoringConfiguration.maxFinishedRacesForScoring);
      this.inputForm.controls.attendanceBonusType.setValue(this.scoringConfiguration.attendanceBonusType);
      this.inputForm.controls.fixedAttendanceBonus.setValue(this.scoringConfiguration.fixedAttendanceBonus);
      this.inputForm.controls.registrationDeadline.setValue(this.scoringConfiguration.registrationDeadline);
    }
  }

  private minLessThanMaxValidator() {
    return (control: any) => {
      const group = control as FormGroup;

      const min = Number(group.get('minFinishedRacesForScoring')?.value);
      const max = Number(group.get('maxFinishedRacesForScoring')?.value);

      if (!isNaN(min) && !isNaN(max) && min && max && min >= max) {
        return { minNotLessThanMax: true };
      }
      return null;
    };
  }

  private variableAttendanceBonusRaceNumberValidator() {
    return (control: any) => {
      const group = control as FormGroup;

      const maxFinished = Number(group.get('maxFinishedRacesForScoring')?.value);
      const bonuses = group.get('variableAttendanceBonuses') as FormArray;

      if (!bonuses) return null;

      const raceNumbers = bonuses.controls.map(c => Number(c.get('raceNumber')?.value));

      const duplicateIndexes: number[] = [];
      const invalidStartIndexes: number[] = [];

      raceNumbers.forEach((rn, i, arr) => {
        if (rn != null && arr.indexOf(rn) !== i) {
          duplicateIndexes.push(i);
        }
      });

      const minValidStart = maxFinished + 1;
      raceNumbers.forEach((rn, i) => {
        if (rn != null && rn < minValidStart) {
          invalidStartIndexes.push(i);
        }
      });

      const errors: any = {};
      if (duplicateIndexes.length) errors.duplicateRaceIndexes = duplicateIndexes;
      if (invalidStartIndexes.length) errors.invalidRaceStartIndexes = invalidStartIndexes;
      return Object.keys(errors).length ? errors : null;
    };
  }




  get variableAttendanceBonuses(): FormArray {
    return this.inputForm.get('variableAttendanceBonuses') as FormArray;
  }

  variableAttendanceBonusFormControl(i: number): FormGroup {
    return this.variableAttendanceBonuses.at(i) as FormGroup;
  }

  clearAttendanceBonus() {
    this.variableAttendanceBonuses.clear();
  }

  addVariableAttendanceBonus(v?: VariableAttendanceBonus) {
    const maxFinishedRaces = Number(this.inputForm.controls.maxFinishedRacesForScoring.value);
    const length = this.variableAttendanceBonuses.controls.length;

    const raceNumber = v?.raceNumber ?? ((maxFinishedRaces + (length ?? 0)) + 1);

    this.variableAttendanceBonuses.push(
        this.formBuilder.group({
          id: [v?.id ?? null],
          raceNumber: [raceNumber, required],
          bonusPoints: [v?.bonusPoints ?? null, required],
        })
    );

    this.inputForm.updateValueAndValidity();
  }

  deleteVariableAttendanceBonus(index: number) {
    this.variableAttendanceBonuses.removeAt(index);
    if (!((this.variableAttendanceBonuses.controls.length ?? 0) > 0)) {
      this.addVariableAttendanceBonus();
    }
    this.inputForm.updateValueAndValidity();
  }


  onClose(): void {
    this.activeModal.dismiss();
  }

  onSaveButtonClick(): void {
    if (!this.inputForm.valid) {
      this.markAllAsTouched(this.inputForm);
      return;
    }
    else {
      let scoringConfig : IndividualScoringConfiguration = {...this.scoringConfiguration};
      scoringConfig.name = this.inputForm.controls.name.value;
      const scoringDefinitionId = this.inputForm.controls.scoringDefinition.value;
      const scoringDefinition = this.scoringDefinitions.find(
          definition => definition.id === scoringDefinitionId
      );

      scoringConfig.scoringDefinition = scoringDefinition;
      scoringConfig.minFinishedRacesForScoring = this.inputForm.controls.minFinishedRacesForScoring.value;
      scoringConfig.maxFinishedRacesForScoring = this.inputForm.controls.maxFinishedRacesForScoring.value;
      scoringConfig.attendanceBonusType = this.inputForm.controls.attendanceBonusType.value;
      scoringConfig.fixedAttendanceBonus = this.inputForm.controls.fixedAttendanceBonus.value;
      scoringConfig.registrationDeadline = this.inputForm.controls.registrationDeadline.value;

      let variableAttendanceBonuses: VariableAttendanceBonus[] = [];

      const bonusesArray = this.inputForm.get('variableAttendanceBonuses') as FormArray;
      if (bonusesArray) {
        bonusesArray.controls.forEach(ctrl => {
          const fg = ctrl as FormGroup;
          variableAttendanceBonuses.push({
            id: fg.get('id')?.value,
            raceNumber: fg.get('raceNumber')?.value,
            bonusPoints: fg.get('bonusPoints')?.value
          });
        });
      }

      scoringConfig.variableAttendanceBonuses = variableAttendanceBonuses;
      this.onSave.emit(scoringConfig);
    }
  }

  protected readonly AttendanceBonusType = AttendanceBonusType;
}